import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Form } from '../../design';
import { useRequester } from '../../hooks';
import CharacterResource from '../../resources/CharacterResource';
import useConfirmation from '../../hooks/useConfirmation';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: (character: CharacterResource) => void;
  character?: CharacterResource | null;
};

export default function CharacterFormModal({
  open,
  onClose,
  onSuccess,
  character,
}: Props) {
  const isEditMode = Boolean(character);

  const deleteCharacterConfirmation = useConfirmation<CharacterResource>({
    getTitle: (char) => `Delete Character "${char.data.name || char.data.id}"?`,
    getDescription: () =>
      'Are you sure you want to delete this character? Participants linked to it will lose their character.',
    onConfirm: async (char) => {
      try {
        await CharacterResource.deleteOne(char.data.id);
        CharacterResource.showAlert('Character deleted successfully', 'success');
        onSuccess(char);
        onClose();
      } catch {
        CharacterResource.showAlert('Failed to delete character', 'error');
      }
    },
  });

  const saveCharacter = useRequester<CharacterResource>({
    onSubmit: (data: any) =>
      isEditMode && character
        ? CharacterResource.updateOne(character.data.id, data)
        : CharacterResource.createOne(data),
    onSuccess: (savedCharacter) => {
      onSuccess(savedCharacter);
      onClose();
    },
    onError: console.log,
  });

  const handleSubmit = (data: any) => {
    const voice = {
      service: data.voiceService,
      code: data.voiceCode,
      accent: data.voiceAccent,
      speed: {
        beginner: parseFloat(data.voiceSpeedBeginner) || 1,
        intermediate: parseFloat(data.voiceSpeedIntermediate) || 1,
        advanced: parseFloat(data.voiceSpeedAdvanced) || 1,
      },
    };

    const body = {
      name: data.name,
      avatar: data.avatar,
      voice,
    };

    saveCharacter.submit(body);
  };

  const voice = character?.data.voice;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="bg-white absolute top-1/2 left-1/2 w-[800px] transform -translate-x-1/2 -translate-y-1/2 p-8 gap-4 flex flex-col">
        <Box className="flex justify-between">
          <Box className="flex flex-row">
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              {isEditMode ? 'Edit Character' : 'Create New Character'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Form
          key={character?.data.id || 'new'}
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
          disabled={saveCharacter.loading}
        >
          <TextField
            name="name"
            label="Name"
            fullWidth
            size="small"
            defaultValue={character?.data.name || ''}
          />
          <TextField
            name="avatar"
            label="Avatar URL"
            fullWidth
            size="small"
            defaultValue={character?.data.avatar || ''}
          />

          <Divider />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Voice
          </Typography>
          <Box className="grid grid-cols-3 gap-4">
            <FormControl fullWidth size="small">
              <InputLabel id="select-voice-service">Voice Service</InputLabel>
              <Select
                labelId="select-voice-service"
                label="Voice Service"
                defaultValue={voice?.service || 'google'}
                name="voiceService"
              >
                <MenuItem value="google">Google</MenuItem>
                <MenuItem value="openai">Open AI</MenuItem>
                <MenuItem value="elevenLabs">ElevenLabs</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Voice Id"
              name="voiceCode"
              fullWidth
              size="small"
              defaultValue={voice?.code || ''}
            />
            <TextField
              label="Accent"
              name="voiceAccent"
              fullWidth
              size="small"
              defaultValue={voice?.accent || ''}
            />
            <TextField
              type="number"
              label="Beginner"
              name="voiceSpeedBeginner"
              fullWidth
              size="small"
              defaultValue={voice?.speed?.beginner ?? 1}
              inputProps={{
                step: 0.01,
                min: 0.25,
                max: 4,
              }}
            />
            <TextField
              type="number"
              label="Intermediate"
              name="voiceSpeedIntermediate"
              fullWidth
              size="small"
              defaultValue={voice?.speed?.intermediate ?? 1}
              inputProps={{
                step: 0.01,
                min: 0.25,
                max: 4,
              }}
            />
            <TextField
              type="number"
              label="Advanced"
              name="voiceSpeedAdvanced"
              fullWidth
              size="small"
              defaultValue={voice?.speed?.advanced ?? 1}
              inputProps={{
                step: 0.01,
                min: 0.25,
                max: 4,
              }}
            />
          </Box>

          <Divider />
          <Box className="flex flex-row gap-4 items-center">
            <Button
              startIcon={
                saveCharacter.loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <SaveIcon sx={{ height: 20, width: 20 }} />
                )
              }
              type="submit"
              variant="contained"
              disabled={saveCharacter.loading}
            >
              {isEditMode ? 'Save' : 'Create'}
            </Button>
            {isEditMode && character && (
              <Button
                variant="outlined"
                color="error"
                disabled={saveCharacter.loading}
                onClick={() => deleteCharacterConfirmation.triggerFor(character)}
              >
                Delete
              </Button>
            )}
            {saveCharacter.error && (
              <Alert severity="error">{saveCharacter.error}</Alert>
            )}
          </Box>
        </Form>
      </Box>
    </Modal>
  );
}
